import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { handleGetRequest, handlePostRequest } from '../utils/api-handler.util'
import { showToaster } from '../utils/show-toaster.util'
import {
  NOTIFICATION_POLLING_INTERVAL,
  NOTIFICATION_STORAGE_KEY,
  NOTIFICATION_TYPES,
} from '../constants/notification'
import USER_TYPE from '../constants/userType'
import { QueryKeys } from '../utils/queryKeyGenerator'

export const useNotification = () => {
  const { isAuthenticated, userType } = useAuth()

  const getStoredNotifications = () => {
    try {
      const stored = localStorage.getItem(NOTIFICATION_STORAGE_KEY)
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.error('Error parsing stored notifications:', error)
      return {}
    }
  }

  const saveNotificationsToStorage = (data) => {
    try {
      localStorage.setItem(NOTIFICATION_STORAGE_KEY, JSON.stringify(data))
    } catch (error) {
      console.error('Error saving notifications to storage:', error)
    }
  }

  const compareAndToastNotifications = (newData, oldData) => {
    Object.values(NOTIFICATION_TYPES).forEach(({ key }) => {
      const newCount = newData[key] || 0
      const oldCount = oldData[key] || 0

      if (newCount > oldCount) {
        const difference = newCount - oldCount
        const message = NOTIFICATION_TYPES[key].message(difference)
        showToaster('success', message)
      }
    })
  }

  const fetchNotifications = async () => {
    if (!isAuthenticated() || !userType || userType === USER_TYPE.guest) {
      return null
    }

    const endpoint = `/${userType}s/notifications`
    const response = await handleGetRequest(endpoint)

    if (response?.success && response?.data) {
      const oldData = getStoredNotifications()
      compareAndToastNotifications(response.data, oldData)
      saveNotificationsToStorage(response.data)
      return response.data
    }

    return null
  }

  const { data: notificationData } = useQuery({
    queryKey: QueryKeys.notifications(userType),
    queryFn: fetchNotifications,
    enabled: isAuthenticated() && userType && userType !== USER_TYPE.guest,
    refetchInterval: NOTIFICATION_POLLING_INTERVAL,
    refetchOnWindowFocus: true,
  })

  const getNotificationCount = (type) => {
    return notificationData?.[type] || 0
  }

  const getTotalNotificationCount = () => {
    if (!notificationData) return 0

    return Object.values(NOTIFICATION_TYPES).reduce((total, { key }) => {
      return total + (notificationData[key] || 0)
    }, 0)
  }

  const getStoredNotificationCount = (type) => {
    const stored = getStoredNotifications()
    return stored[type] || 0
  }

  const clearStoredNotifications = () => {
    localStorage.removeItem(NOTIFICATION_STORAGE_KEY)
  }

  const resetNotificationMutation = useMutation({
    mutationFn: (type) =>
      handlePostRequest(`/${userType}s/notifications/reset`, {
        type,
      }),
    onSuccess: () => {
      QueryKeys.notifications.invalidate(userType)
    },
    onError: (error) => {
      console.error('Failed to reset notification:', error)
    },
  })

  const resetNotification = async (notificationType) => {
    await resetNotificationMutation.mutateAsync(notificationType)
  }

  return {
    data: notificationData,
    getNotificationCount,
    getTotalNotificationCount,
    getStoredNotificationCount,
    clearStoredNotifications,
    resetNotification,
    isResetting: resetNotificationMutation.isPending,
  }
}

export default useNotification
